import { AnimationDuration } from "@/constants/UI";
import useAssetsPicker from "@/hooks/useAssetsPicker";
import useAppMonitor from "@/services/AppMonitor";
import { Button, FAIconName } from "@/ui/core";
import type { ButtonVariant } from "@/ui/core/buttons/Button";
import { filePickerAssetFromImagePickerAsset } from "@/utils/filePicker.util";
import { joinTestIDs } from "@/utils/string.util";

interface PickFromGalleryButtonProps extends ViewWithTestID {
  onAssetPicked: (asset: FilePickerAsset) => Promise<void> | void;
  onPress?: () => void;
  title: string;
  variant?: ButtonVariant;
}

const PickFromGalleryButton = ({
  onAssetPicked,
  onPress,
  testID,
  title,
  variant,
}: PickFromGalleryButtonProps) => {
  const { pickImageFromGallery } = useAssetsPicker();
  const { captureException } = useAppMonitor();

  const handlePickImageFromGallery = async () => {
    try {
      const galleryAsset = await pickImageFromGallery({
        onError: captureException,
      });

      if (!galleryAsset) {
        return;
      }

      const pickerAsset = filePickerAssetFromImagePickerAsset(galleryAsset);
      await onAssetPicked(pickerAsset);
    } catch (error) {
      captureException(error);
    }
  };

  const handlePress = () => {
    onPress?.();
    // Wait for the bottom-sheet dismiss animation to finish before launching the
    // native gallery. Launching immediately races the dismiss, so the sheet can
    // re-present on top of the picker (overlapping modal presentations).
    setTimeout(() => {
      void handlePickImageFromGallery();
    }, AnimationDuration.ModalDismiss);
  };

  return (
    <Button
      isFullWidth
      leftIcon={FAIconName.Images}
      onPress={handlePress}
      testID={joinTestIDs(testID, "pick-from-gallery")}
      title={title}
      variant={variant}
    />
  );
};

export default PickFromGalleryButton;
